import RiyasolIcon from "@/components/icons/RiyasolIcon"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { Skeleton } from "@/components/ui/skeleton"
import { House, List } from "lucide-react"
import {
  useCanAccess,
  useCreatePath,
  useGetResourceLabel,
  useHasDashboard,
  useResourceDefinitions,
  useTranslate,
} from "ra-core"
import { createElement } from "react"
import { Link, useMatch } from "react-router"

export function AppSidebar() {
  const hasDashboard = useHasDashboard()
  const resources = useResourceDefinitions()
  const { openMobile, setOpenMobile } = useSidebar()
  const handleClick = () => {
    if (openMobile) {
      setOpenMobile(false)
    }
  }
  return (
    <Sidebar variant="floating" collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              className="data-[slot=sidebar-menu-button]:p-1.5!"
            >
              <Link to="/">
                <RiyasolIcon />
                <span className="text-base font-semibold">Riyasol</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {hasDashboard ? (
                <DashboardMenuItem onClick={handleClick} />
              ) : null}
              {Object.keys(resources)
                .filter(name => resources[name].hasList)
                .map(name => (
                  <ResourceMenuItem
                    key={name}
                    name={name}
                    onClick={handleClick}
                  />
                ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter />
    </Sidebar>
  )
}

export const DashboardMenuItem = ({ onClick }: { onClick?: () => void }) => {
  const translate = useTranslate()
  const label = translate("ra.page.dashboard", {
    _: "Dashboard",
  })
  const match = useMatch({ path: "/", end: true })
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={!!match}>
        <Link to="/" onClick={onClick}>
          <House />
          {label}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

export const ResourceMenuItem = ({
  name,
  onClick,
}: {
  name: string
  onClick?: () => void
}) => {
  const { canAccess, isPending } = useCanAccess({
    resource: name,
    action: "list",
  })
  const resources = useResourceDefinitions()
  const getResourceLabel = useGetResourceLabel()
  const createPath = useCreatePath()
  const to = createPath({
    resource: name,
    type: "list",
  })
  const match = useMatch({ path: to, end: false })

  if (isPending) {
    return <Skeleton className="h-8 w-full" />
  }

  if (!resources || !resources[name] || !canAccess) return null

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={!!match}>
        <Link to={to} state={{ _scrollToTop: true }} onClick={onClick}>
          {resources[name].icon ? (
            createElement(resources[name].icon)
          ) : (
            <List />
          )}
          {getResourceLabel(name, 2)}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}
